import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Folder,
  Grid,
  List,
  Plus,
  Search,
  Loader2,
  Filter,
  Upload,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  ChevronDown,
  RotateCcw,
  SlidersHorizontal,
  Calendar,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import "../../css/RepoOrganizer.css";
import { useTableSort } from "../../hooks/useTableSort";
import { SortableHeader } from "../../components/SortableHeader";
import { canModifyRepoItem } from "./utils/canModifyRepoItem";
import { getFileIcon } from "./utils/getFileIcon";
import { RepoDragPreview } from "./components/RepoDragPreview";
import { DraggableItem } from "./components/DraggableItem";
import { RepoBreadcrumbs } from "./components/RepoBreadcrumbs";
import { RepoFloatingActions } from "./components/RepoFloatingActions";
import { RepoModals } from "./components/RepoModals";
import { useRepoItems } from "./hooks/useRepoItems";
import { useRepoFilterState } from "./hooks/useRepoFilterState";
import { useFilteredRepoItems } from "./hooks/useFilteredRepoItems";
import { useRepoMarqueeSelection } from "./hooks/useRepoMarqueeSelection";
import { useRepoItemActions } from "./hooks/useRepoItemActions";
import { setRefreshSidebar } from "../../redux/sidebarSlice";
import { setSessionId as setReduxSessionId } from "../../redux/sessionSlice";
import api from "../../api";
import { showErrorToast } from "../../utils/toastUtils";
import { getItemLocationPath } from "./utils/getItemLocationPath";

import Group_Icon from "../../assets/repo-org/Group_Icon.svg";
import Group_Icon_Active from "../../assets/repo-org/Group_icon_Active.svg";

import Global_Icon from "../../assets/repo-org/1111.svg";
import Global_Icon_Active from "../../assets/repo-org/Global_icon_Active.svg";

import My_drive_Icon from "../../assets/repo-org/My_drive_Icon.svg";
import My_drive_Icon_Active from "../../assets/repo-org/My_drive_icon_Active.svg";

import Personal_Icon from "../../assets/repo-org/Personal_Icon.svg";
import Personal_Icon_Active from "../../assets/repo-org/Personal_icon_Active.svg";

const REPO_VIEW_STORAGE_KEY = "repo-organizer.selected-repo-view";

const getInitialRepoView = (role) => {
  if (typeof window === "undefined") {
    return role === "admin" ? "global" : "global";
  }

  const savedView = window.localStorage.getItem(REPO_VIEW_STORAGE_KEY);
  const allowedViews =
    role === "admin"
      ? new Set(["global", "group"])
      : new Set(["global", "group", "personal", "personal_space"]);

  if (savedView && allowedViews.has(savedView)) {
    return savedView;
  }

  return role === "admin" ? "global" : "global";
};

const RepoOrganizer = ({ role = "user" }) => {
  const [activeSection, setActiveSection] = useState("repository");
  const [viewMode, setViewMode] = useState("list");
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: "Root" }]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [repoView, setRepoView] = useState(() => getInitialRepoView(role));
  // Tracks which group is currently open in Group Repository view.
  // null = showing the group list; non-null = showing contents of that group.
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("Saved");
  const [deleteModal, setDeleteModal] = useState({ show: false, items: [] });
  const [moveModal, setMoveModal] = useState({
    show: false,
    items: [],
    targetFolderId: null,
  });
  const [createFolderModal, setCreateFolderModal] = useState({
    show: false,
    name: "",
  });
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  const fileInputRef = useRef(null);
  const isPersonalSpaceView = repoView === "personal_space";
  const isGroupView = repoView === "group";
  const isGroupRootLevel = isGroupView && !selectedGroupId;
  const disableSelectionControls = isGroupRootLevel;
  const showDocStatusColumn = !isGroupRootLevel && !isPersonalSpaceView;
  const showStatusColumn = !isGroupRootLevel && !isPersonalSpaceView;
  const showActionsColumn = !isGroupRootLevel;

  const filterState = useRepoFilterState();
  const {
    searchTerm,
    setSearchTerm,
    typeFilters,
    dayFilter,
    setDayFilter,
    showFilterDropdown,
    setShowFilterDropdown,
    tempSortOrder,
    setTempSortOrder,
    tempDayFilter,
    setTempDayFilter,
    tempFolderSort,
    setTempFolderSort,
    appliedSortOrder,
    setAppliedSortOrder,
    appliedFolderSort,
    setAppliedFolderSort,
    filterDropdownRef,
    handleApplyFilter,
    handleResetFilter,
    handleTypeFilterChange,
  } = filterState;

  const { items, setItems, isLoading, setIsLoading, fetchItems } = useRepoItems(
    {
      role,
      isPersonalSpaceView,
      statusFilter,
      dayFilter,
      appliedSortOrder,
      isGroupView,
      selectedGroupId,
    },
  );

  const isSearchActive = searchTerm.trim().length > 0;

  const itemLocationPaths = useMemo(() => {
    if (!isSearchActive) return null;

    const paths = new Map();
    items.forEach((item) => {
      paths.set(item.id, getItemLocationPath(item, items, repoView));
    });
    return paths;
  }, [isSearchActive, items, repoView]);

  const filteredItems = useFilteredRepoItems(items, {
    repoView,
    currentFolder,
    searchTerm,
    dayFilter,
    typeFilters,
    appliedSortOrder,
    appliedFolderSort,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedModel, selectedMode } = useSelector((state) => state.model);

  const { containerRef, dragSelectionRef, selectionRect, handleMouseDown } =
    useRepoMarqueeSelection(filteredItems, setSelectedItems);

  const actions = useRepoItemActions({
    items,
    setItems,
    fetchItems,
    role,
    repoView,
    currentFolder,
    groupId: selectedGroupId,
    createFolderModal,
    setCreateFolderModal,
    deleteModal,
    setDeleteModal,
    setSelectedItems,
  });

  const { sortedData, requestSort, sortConfig } = useTableSort(filteredItems, {
    foldersFirst: appliedFolderSort === "Top",
    defaultSortKey: "approvalStatus",
    defaultSortDirection: "asc",
  });

  const totalPages = Math.ceil(sortedData.length / entriesPerPage);
  const paginatedItems = sortedData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const paginationDisplay = (() => {
    if (totalPages <= 0) {
      return {
        pages: [],
        showLeadingEllipsis: false,
        showTrailingEllipsis: false,
      };
    }

    const startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, currentPage + 1);

    const pages = [];
    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(page);
    }

    return {
      pages,
      showLeadingEllipsis: startPage > 1,
      showTrailingEllipsis: endPage < totalPages,
    };
  })();

  const isBlockedDocumentSelection = (item) =>
    role !== "admin" &&
    item?.type === "file" &&
    ["duplicate", "pending", "rejected"].includes(
      String(item?.approvalStatus || "").toLowerCase(),
    );
  const selectablePaginatedItems = paginatedItems.filter(
    (item) => !isBlockedDocumentSelection(item),
  );

  const resetViewContext = () => {
    setCurrentFolder(null);
    setBreadcrumbs([{ id: null, name: "Root" }]);
    setSelectedItems(new Set());
    setSelectedGroupId(null);
  };

  const isChatEligibleRepoItem = (item) =>
    item?.type === "file" &&
    !["webpage", "youtube_audio", "url", "video"].includes(
      String(item?.fileType || "").toLowerCase(),
    );

  const isPersonalRepoItem = (item) =>
    item?.isPersonalSpace === true ||
    (!item?.isPersonalSpace &&
      item?.ownerType !== "admin" &&
      item?.mode !== "global" &&
      item?.mode !== "group");

  useEffect(() => {
    setSelectedItems((prevSelectedItems) => {
      let hasBlockedSelection = false;
      const blockedIds = new Set(
        items.filter(isBlockedDocumentSelection).map((item) => item.id),
      );

      const nextSelectedItems = new Set();
      prevSelectedItems.forEach((id) => {
        if (blockedIds.has(id)) {
          hasBlockedSelection = true;
          return;
        }
        nextSelectedItems.add(id);
      });

      return hasBlockedSelection ? nextSelectedItems : prevSelectedItems;
    });
  }, [items, role, selectedItems, setSelectedItems]);

  const handleChatWithSelectedFiles = async () => {
    const selectedKbEntries = selectedItemDetails
      .filter(isChatEligibleRepoItem)
      .map((item) => {
        const filename = String(item?.name || "").trim();
        if (!filename) return null;
        const rawDocId = item?.document_id ?? item?.documentId ?? null;
        const document_id =
          rawDocId == null || String(rawDocId).trim() === ""
            ? null
            : String(rawDocId);
        return { filename, document_id };
      })
      .filter(Boolean);

    if (selectedKbEntries.length === 0) return;
    if (!selectedModel) {
      showErrorToast("Select a model first");
      return;
    }

    try {
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/chat/new-session`,
        {
          model_id: selectedModel,
          mode: selectedMode,
          knowledge_search_enabled: true,
          group_ids: [],
          knowledge_base_files: selectedKbEntries,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      const newSessionId = res.data?.session_id;
      if (!newSessionId) throw new Error("Missing session_id");

      try {
        sessionStorage.setItem(
          "kb_pending_selected_files",
          JSON.stringify(selectedKbEntries),
        );
        sessionStorage.setItem(
          "kb_pending_selected_groups",
          JSON.stringify([]),
        );
        sessionStorage.setItem("kb_auto_open_dropdown", "1");
        sessionStorage.setItem("kb_keep_selection_for_next_session", "1");
      } catch {
        // ignore sessionStorage failures
      }

      dispatch(setReduxSessionId(newSessionId));
      dispatch(setRefreshSidebar(true));
      navigate("/user/new", {
        state: { sessionId: newSessionId },
        replace: true,
      });
    } catch (err) {
      console.error("Failed to create chat session from selected files:", err);
      showErrorToast("Failed to create chat session");
    }
  };

  const onNavigate = (folderId, folderName) => {
    // When navigating into a group root folder, set selectedGroupId
    // so useRepoItems fetches that group's contents.
    if (isGroupView && folderId && folderId.startsWith("group-")) {
      const gId = folderId.replace("group-", "");
      setSelectedGroupId(gId);
    }
    // When navigating back to root in group view, clear selectedGroupId
    if (isGroupView && !folderId) {
      setSelectedGroupId(null);
    }
    actions.handleNavigate(
      folderId,
      folderName,
      setCurrentFolder,
      setBreadcrumbs,
    );
    setSearchTerm("");
  };

  console.log("selectedGroupId", selectedGroupId);
  console.log("fetching group contents", selectedGroupId);

  const onAction = (action, item) => {
    actions.handleAction(action, item, setMoveModal);
  };

  const selectedItemDetails = items.filter((i) => selectedItems.has(i.id));
  const selectedDraggableItems = selectedItemDetails.filter((item) =>
    canModifyRepoItem(item, role),
  );
  const hasSelectedFolder = selectedItemDetails.some(
    (i) => i.type === "folder",
  );
  const selectedEligibleItems = selectedItemDetails.filter(
    isChatEligibleRepoItem,
  );
  const selectedEligibleCount = selectedEligibleItems.length;
  const allSelectedItemsEligible =
    selectedItemDetails.length > 0 &&
    selectedEligibleCount === selectedItemDetails.length;
  const allSelectedPersonalItems =
    selectedItemDetails.length > 0 &&
    selectedItemDetails.every(isPersonalRepoItem);
  const allSelectedDeletedPersonalItems =
    allSelectedPersonalItems &&
    selectedItemDetails.every((item) => String(item.fileStatus) !== "1");
  const canModifySelectedItems = selectedItemDetails.some((item) =>
    canModifyRepoItem(item, role),
  );
  const showRepoFloatingActions =
    !isGroupRootLevel &&
    selectedItemDetails.some(
      (item) => item.type === "file" || item.type === "folder",
    );

  const [openActionMenuItemId, setOpenActionMenuItemId] = useState(null);

  const draggableItemProps = {
    viewMode,
    showLocation: isSearchActive,
    onNavigate,
    onToggleSelect: (id) => {
      if (isGroupRootLevel) return;
      setOpenActionMenuItemId(null);
      actions.toggleSelect(id, setSelectedItems);
    },
    onAction,
    getFileIcon,
    onMove: actions.handleMove,
    role,
    repoView,
    dragSelectionRef,
    selectedDragItems: selectedDraggableItems,
    onApprovalStatusChange:
      role === "admin" ? actions.handleChangeApprovalStatus : undefined,
    updatingItems: actions.updatingItems,
    openActionMenuItemId,
    onToggleActionMenu: (id) =>
      setOpenActionMenuItemId((current) => (current === id ? null : id)),
    onOpenActionMenu: (id) => setOpenActionMenuItemId(id),
    onCloseActionMenu: () => setOpenActionMenuItemId(null),
    disableContextMenu: isGroupRootLevel,
    hideActions: isGroupRootLevel,
    disableSelection: isGroupRootLevel,
  };

  // console.log(
  //   "Selected item types:",
  //   selectedItemDetails.map((item) => ({
  //     name: item.name,
  //     type: item.type,
  //     fileType: item.fileType,
  //     sourceType: item.sourceType,
  //   })),
  // );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  console.log("repoView    ", repoView);

  const repoDescriptions = {
    global:
      "Global Repository – Organization-wide approved knowledge and documents that have been AI-trained and are searchable across the enterprise.",

    group:
      "Group Repository – Team-specific knowledge and shared content that have been AI-trained for streamlined discovery and retrieval.",

    personal:
      "Personal Repository – Your personal collection of AI-trained documents and knowledge assets available for search and insights.",

    personal_space:
      "My Drive – A private workspace for storing and managing content before it is trained and published to a repository.",
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(REPO_VIEW_STORAGE_KEY, repoView);
  }, [repoView]);

  return (
    <DndProvider backend={HTML5Backend}>
      <RepoDragPreview />
      <div
        ref={containerRef}
        className="repo-container"
        onMouseDown={
          activeSection === "repository" && !isGroupRootLevel
            ? handleMouseDown
            : undefined
        }
      >
        <div className="repo-glass-panel repo-animate-fade">
          <div className="d-flex align-items-center justify-content-between repo-tabs-parent">
            <div className="repo-tabs">
              <button
                className={`repo-tab d-flex align-items-center gap-2  ${
                  activeSection === "repository" && repoView === "global"
                    ? "active"
                    : ""
                }`}
                onClick={() => {
                  setActiveSection("repository");
                  setRepoView("global");
                  resetViewContext();
                }}
              >
                <img
                  src={
                    activeSection === "repository" && repoView === "global"
                      ? Global_Icon_Active
                      : Global_Icon
                  }
                  alt="Global_Icon"
                  className="repo-icon"
                />
                Global
              </button>
              {/* Group Repository tab — visible to both users and admins */}
              <button
                className={`repo-tab d-flex align-items-center gap-2 ${
                  activeSection === "repository" && repoView === "group"
                    ? "active"
                    : ""
                }`}
                onClick={() => {
                  setActiveSection("repository");
                  setRepoView("group");
                  resetViewContext();
                }}
              >
                <img
                  src={
                    activeSection === "repository" && repoView === "group"
                      ? Group_Icon_Active
                      : Group_Icon
                  }
                  alt="Group_Icon"
                  className="repo-icon"
                />
                Group
              </button>
              {role !== "admin" && (
                <button
                  className={`repo-tab d-flex align-items-center gap-2  ${
                    activeSection === "repository" && repoView === "personal"
                      ? "active"
                      : ""
                  }`}
                  onClick={() => {
                    setActiveSection("repository");
                    setRepoView("personal");
                    resetViewContext();
                  }}
                >
                  <img
                    src={
                      activeSection === "repository" && repoView === "personal"
                        ? Personal_Icon_Active
                        : Personal_Icon
                    }
                    alt="Personal_Icon"
                    className="repo-icon"
                  />
                  Personal
                </button>
              )}
              {role !== "admin" && (
                <button
                  className={`repo-tab d-flex align-items-center gap-2 ${
                    activeSection === "repository" &&
                    repoView === "personal_space"
                      ? "active"
                      : ""
                  }`}
                  onClick={() => {
                    setActiveSection("repository");
                    setRepoView("personal_space");
                    resetViewContext();
                  }}
                >
                  <img
                    src={
                      activeSection === "repository" &&
                      repoView === "personal_space"
                        ? My_drive_Icon_Active
                        : My_drive_Icon
                    }
                    alt="My_drive_Icon"
                    className="repo-icon"
                  />
                  My Drive
                </button>
              )}
            </div>
            {/* <div className="repo-tabs">
              {role !== "admin" && (
                <button
                  type="button"
                  className="repo-type-chip active m-0"
                  onClick={() => {
                    navigate("/user/training-studio");
                  }}
                >
                  Training Studio
                </button>
              )}
            </div> */}
          </div>
          <div className="d-flex align-items-center justify-content-end justify-content-sm-between repo-tabs-parent border-bottom ">
            <div className="repo-tabs ak-text-md refresh-prompts d-none d-sm-block">
              {repoDescriptions[repoView]}
            </div>

            <div className="repo-tabs">
              {role !== "admin" && (
                <button
                  type="button"
                  className="repo-type-chip active m-0"
                  onClick={() => {
                    navigate("/user/training-studio");
                  }}
                >
                  Training Studio
                </button>
              )}
            </div>
          </div>

          <>
            <div className="repo-header">
              <RepoBreadcrumbs
                breadcrumbs={breadcrumbs}
                onNavigate={onNavigate}
              />

              <div className="repo-actions">
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  multiple
                  onChange={(e) => actions.handleFileChange(e, setIsLoading)}
                />

                {repoView !== "personal_space" && repoView !== "group" && (
                  <>
                    {isMobile ? (
                      <select
                        className="repo-type-dropdown"
                        value={typeFilters[0]}
                        onChange={(e) =>
                          handleTypeFilterChange(e.target.value, setCurrentPage)
                        }
                      >
                        <option value="all">All</option>
                        <option value="document">Document</option>
                        <option value="video">Videos</option>
                        <option value="url">URLs</option>

                        {role === "admin" && (
                          <>
                            <option value="text">Text</option>
                            <option value="qa">Q&A</option>
                          </>
                        )}
                      </select>
                    ) : (
                      <div className="repo-type-filters">
                        <button
                          className={`repo-type-chip ${typeFilters.includes("all") ? "active" : ""}`}
                          onClick={() =>
                            handleTypeFilterChange("all", setCurrentPage)
                          }
                        >
                          All
                        </button>
                        <button
                          className={`repo-type-chip ${typeFilters.includes("document") ? "active" : ""}`}
                          onClick={() =>
                            handleTypeFilterChange("document", setCurrentPage)
                          }
                        >
                          Document
                        </button>
                        <button
                          className={`repo-type-chip ${typeFilters.includes("video") ? "active" : ""}`}
                          onClick={() =>
                            handleTypeFilterChange("video", setCurrentPage)
                          }
                        >
                          Videos
                        </button>
                        <button
                          className={`repo-type-chip ${typeFilters.includes("url") ? "active" : ""}`}
                          onClick={() =>
                            handleTypeFilterChange("url", setCurrentPage)
                          }
                        >
                          URLs
                        </button>
                        {role === "admin" && (
                          <>
                            <button
                              className={`repo-type-chip ${typeFilters.includes("text") ? "active" : ""}`}
                              onClick={() =>
                                handleTypeFilterChange("text", setCurrentPage)
                              }
                            >
                              Text
                            </button>
                            <button
                              className={`repo-type-chip ${typeFilters.includes("qa") ? "active" : ""}`}
                              onClick={() =>
                                handleTypeFilterChange("qa", setCurrentPage)
                              }
                            >
                              Q&A
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}

                {repoView === "personal_space" && (
                  <div className="repo-type-filters">
                    <button
                      className={`repo-type-chip ${statusFilter === "Saved" ? "active" : ""}`}
                      onClick={() => {
                        setStatusFilter("Saved");
                        setCurrentPage(1);
                      }}
                    >
                      Saved
                    </button>
                    <button
                      className={`repo-type-chip ${statusFilter === "Deleted" ? "active" : ""}`}
                      onClick={() => {
                        setStatusFilter("Deleted");
                        setCurrentPage(1);
                      }}
                    >
                      Deleted
                    </button>
                  </div>
                )}

                {/* New Folder button:
                    - Hidden in group view at root (cannot create root groups manually)
                    - Hidden in group view for non-admins (users cannot create folders)
                    - Shown in group view for admins when inside a group (currentFolder is set)
                    - Shown for personal/global views as before
                */}
                {!(
                  // Hide if user is non-admin in any group view position
                  (
                    (isGroupView && role !== "admin") ||
                    // Hide for admins at the group list root (no group selected yet)
                    (isGroupView && role === "admin" && !selectedGroupId)
                  )
                ) &&
                  !(repoView === "global" && role !== "admin") && (
                    <button
                      className={`repo-btn repo-btn-primary ${statusFilter === "Deleted" ? "" : ""}`}
                      onClick={() =>
                        setCreateFolderModal({ show: true, name: "" })
                      }
                      title={
                        repoView === "personal_space" &&
                        statusFilter === "Deleted"
                          ? "Cannot create folders in Deleted view"
                          : "New Folder"
                      }
                      disabled={
                        repoView === "personal_space" &&
                        statusFilter === "Deleted"
                      }
                      style={{
                        opacity:
                          repoView === "personal_space" &&
                          statusFilter === "Deleted"
                            ? 0.5
                            : 1,
                      }}
                    >
                      <Plus size={18} />
                    </button>
                  )}
                {repoView === "personal_space" && (
                  <button
                    className="repo-btn repo-btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    title="Upload Files"
                  >
                    <Upload size={18} />
                  </button>
                )}

                <div className="repo-view-toggle">
                  <button
                    className={`repo-view-btn ${viewMode === "grid" ? "active" : ""}`}
                    onClick={() => {
                      setViewMode("grid");
                      setSelectedItems(new Set());
                    }}
                  >
                    <Grid size={18} />
                  </button>
                  <button
                    className={`repo-view-btn ${viewMode === "list" ? "active" : ""}`}
                    onClick={() => {
                      setViewMode("list");
                      setSelectedItems(new Set());
                    }}
                  >
                    <List size={18} />
                  </button>
                </div>
              </div>
            </div>

            {showRepoFloatingActions ? (
              <RepoFloatingActions
                selectedCount={selectedItems.size}
                hasSelectedFolder={hasSelectedFolder}
                canModifySelectedItems={canModifySelectedItems}
                allSelectedPersonalItems={allSelectedPersonalItems}
                allSelectedDeletedPersonalItems={
                  allSelectedDeletedPersonalItems
                }
                role={role}
                selectedFileCount={selectedEligibleCount}
                showChatWithSelectedFiles={
                  role !== "admin" &&
                  repoView !== "personal_space" &&
                  allSelectedItemsEligible &&
                  selectedEligibleCount >= 1
                }
                hideDownloadForSelected={selectedItemDetails.some(
                  (item) =>
                    item.fileType === "webpage" ||
                    item.fileType === "youtube_audio" ||
                    item.fileType === "text" ||
                    item.fileType === "qa",
                )}
                hideApprovalForSelected={selectedItemDetails.some(
                  (item) => item.fileType === "text" || item.fileType === "qa",
                )}
                onClearSelection={() => setSelectedItems(new Set())}
                onBulkDownload={() => actions.handleBulkDownload(selectedItems)}
                onChatSelectedFiles={handleChatWithSelectedFiles}
                onOpenMoveModal={() =>
                  setMoveModal({
                    show: true,
                    items: items.filter((i) => selectedItems.has(i.id)),
                    targetFolderId: null,
                  })
                }
                onOpenDeleteModal={() =>
                  setDeleteModal({
                    show: true,
                    items: items.filter((i) => selectedItems.has(i.id)),
                  })
                }
                onBulkRestore={() =>
                  actions.handleBulkRestore(
                    items.filter((i) => selectedItems.has(i.id)),
                  )
                }
                onBulkApprovalStatus={(status) =>
                  actions.handleBulkApprovalStatus(status, selectedItems)
                }
              />
            ) : (
              <div className="repo-filters-bar">
                <div className="repo-filters-left">
                  <div className="repo-entries-select">
                    <select
                      className="repo-select"
                      value={entriesPerPage}
                      onChange={(e) => {
                        setEntriesPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {[10, 25, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    {isMobile ? (
                      <>{/* mobile UI */}</>
                    ) : (
                      <>
                        <span>Entries per page</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="repo-filters-right">
                  <div
                    className="repo-search-container"
                    style={{ position: "relative" }}
                  >
                    <Search
                      size={18}
                      style={{
                        position: "absolute",
                        left: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "var(--repo-text-muted)",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search Documents"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="repo-search-input"
                    />
                  </div>

                  <div
                    className="repo-filter-dropdown-wrapper"
                    ref={filterDropdownRef}
                  >
                    <button
                      className={`repo-filter-toggle-btn ${showFilterDropdown ? "active" : ""}`}
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    >
                      <Filter size={18} />
                    </button>

                    {showFilterDropdown && (
                      <div className="repo-filter-dropdown-menu">
                        {/* Header */}
                        <div className="repo-filter-header">
                          <span className="repo-filter-header-title">
                            Filters
                          </span>
                          <button
                            className="repo-filter-close-btn"
                            onClick={() => setShowFilterDropdown(false)}
                            title="Close"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        {/* Sort Section */}
                        <div className="repo-filter-group">
                          <div className="repo-filter-section-title">
                            <SlidersHorizontal
                              size={14}
                              className="repo-filter-icon"
                            />
                            <span>Sort</span>
                          </div>
                          <div className="repo-filter-select-wrapper">
                            <select
                              value={tempSortOrder}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTempSortOrder(val);
                                setAppliedSortOrder(val);
                                setCurrentPage(1);
                              }}
                              className="repo-filter-select"
                            >
                              <option value="Descending">Newest First</option>
                              <option value="Ascending">Oldest First</option>
                            </select>
                            <ChevronDown
                              size={14}
                              className="repo-filter-select-arrow"
                            />
                          </div>
                        </div>

                        {/* Organization Section */}
                        <div className="repo-filter-group">
                          <div className="repo-filter-section-title">
                            <Folder size={14} className="repo-filter-icon" />
                            <span>Organization</span>
                          </div>
                          <div className="repo-filter-select-wrapper">
                            <select
                              value={tempFolderSort}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTempFolderSort(val);
                                setAppliedFolderSort(val);
                                setCurrentPage(1);
                              }}
                              className="repo-filter-select"
                            >
                              <option value="Top">Folders First</option>
                              <option value="Mixed">Mixed with Files</option>
                            </select>
                            <ChevronDown
                              size={14}
                              className="repo-filter-select-arrow"
                            />
                          </div>
                        </div>

                        {/* Date Section */}
                        <div className="repo-filter-group">
                          <div className="repo-filter-section-title">
                            <Calendar size={14} className="repo-filter-icon" />
                            <span>Date</span>
                          </div>
                          <div className="repo-filter-select-wrapper">
                            <select
                              value={tempDayFilter}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTempDayFilter(val);
                                setDayFilter(val);
                                setCurrentPage(1);
                              }}
                              className="repo-filter-select"
                            >
                              <option value="All">All Time</option>
                              <option value="Today">Today</option>
                              <option value="Yesterday">Yesterday</option>
                            </select>
                            <ChevronDown
                              size={14}
                              className="repo-filter-select-arrow"
                            />
                          </div>
                        </div>

                        {/* Footer Reset */}
                        <div className="repo-filter-footer">
                          <button
                            className="repo-filter-reset-btn"
                            onClick={() => handleResetFilter(setCurrentPage)}
                          >
                            <RotateCcw
                              size={14}
                              className="repo-filter-reset-icon"
                            />
                            <span>Reset Filters</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="repo-loader-container">
                <Loader2 className="animate-spin" size={32} />
                <p className="mt-2 ">Loading repository...</p>
              </div>
            ) : filteredItems.length === 0 ? (
              <div
                className="repo-dropzone"
                onDragOver={(e) => e.preventDefault()}
              >
                <Folder
                  size={48}
                  style={{ marginBottom: "1rem", opacity: 0.3 }}
                />
                <p>No items found in this directory</p>
                <p style={{ fontSize: "0.8rem", opacity: 0.6 }}>
                  Try creating a folder or check other directories
                </p>
              </div>
            ) : viewMode === "grid" ? (
              <div className="repo-grid custom-ak-scollbar">
                {paginatedItems.map((item) => (
                  <DraggableItem
                    key={item.id}
                    item={item}
                    isSelected={selectedItems.has(item.id)}
                    locationPath={itemLocationPaths?.get(item.id) || ""}
                    showDocStatusColumn={showDocStatusColumn}
                    showStatusColumn={showStatusColumn}
                    disableContextMenu={isGroupRootLevel}
                    hideActions={isGroupRootLevel}
                    disableSelection={isGroupRootLevel}
                    {...draggableItemProps}
                  />
                ))}
              </div>
            ) : (
              <>
                <div className="repo-table-wrapper">
                  <div className="repo-table-inner">
                    <table className="repo-list">
                      <thead>
                        <tr>
                          <th style={{ width: "40px" }}>
                            <input
                              type="checkbox"
                              disabled={disableSelectionControls}
                              aria-disabled={disableSelectionControls}
                              onChange={(e) => {
                                if (disableSelectionControls) return;
                                if (e.target.checked) {
                                  setSelectedItems(
                                    new Set(
                                      selectablePaginatedItems.map((i) => i.id),
                                    ),
                                  );
                                } else {
                                  setSelectedItems(new Set());
                                }
                              }}
                              checked={
                                selectablePaginatedItems.length > 0 &&
                                selectablePaginatedItems.every((item) =>
                                  selectedItems.has(item.id),
                                )
                              }
                            />
                          </th>
                          <SortableHeader
                            column="name"
                            label="Name"
                            sortConfig={sortConfig}
                            onSort={requestSort}
                            style={{ width: isSearchActive ? "22%" : "30%" }}
                          />
                          {isSearchActive && (
                            <th
                              className="repo-location-cell"
                              style={{ width: "18%" }}
                            >
                              Location
                            </th>
                          )}
                          <SortableHeader
                            column="type"
                            label="Type"
                            sortConfig={sortConfig}
                            onSort={requestSort}
                            style={{ width: "80px" }}
                          />
                          <SortableHeader
                            column="size"
                            label="Size"
                            sortConfig={sortConfig}
                            onSort={requestSort}
                            style={{ width: "110px" }}
                          />
                          {showDocStatusColumn && (
                            <SortableHeader
                              column="approvalStatus"
                              label="Doc Status"
                              sortConfig={sortConfig}
                              onSort={requestSort}
                              style={{ width: "140px" }}
                            />
                          )}
                          {showStatusColumn && (
                            <SortableHeader
                              column="fileStatus"
                              label="Status"
                              sortConfig={sortConfig}
                              onSort={requestSort}
                              style={{ width: "100px" }}
                            />
                          )}
                          {/* <SortableHeader
                              column="createdAt"
                              label="Uploaded On"
                              sortConfig={sortConfig}
                              onSort={requestSort}
                              style={{ width: "120px" }}
                            /> */}
                          <SortableHeader
                            column="updatedAt"
                            label="Updated At"
                            sortConfig={sortConfig}
                            onSort={requestSort}
                            style={{ width: "120px" }}
                          />
                          {role === "admin" && (
                            <SortableHeader
                              column="uploadedBy"
                              label="Uploaded By"
                              sortConfig={sortConfig}
                              onSort={requestSort}
                              style={{ width: "150px" }}
                            />
                          )}
                          {showActionsColumn && (
                            <th className="action-cell text-center align-middle">
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedItems.map((item) => (
                          <DraggableItem
                            key={item.id}
                            item={item}
                            isSelected={selectedItems.has(item.id)}
                            locationPath={itemLocationPaths?.get(item.id) || ""}
                            showDocStatusColumn={showDocStatusColumn}
                            showStatusColumn={showStatusColumn}
                            disableContextMenu={isGroupRootLevel}
                            hideActions={isGroupRootLevel}
                            disableSelection={isGroupRootLevel}
                            {...draggableItemProps}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="repo-pagination">
                  <div className="repo-pagination-info">
                    Showing{" "}
                    {sortedData.length === 0
                      ? 0
                      : (currentPage - 1) * entriesPerPage + 1}{" "}
                    to{" "}
                    {Math.min(currentPage * entriesPerPage, sortedData.length)}{" "}
                    of {sortedData.length} entries
                  </div>
                  <div className="repo-pagination-controls">
                    <button
                      className="repo-pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                      title="First Page"
                    >
                      <ChevronsLeft size={16} />
                    </button>
                    <button
                      className="repo-pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => prev - 1)}
                      title="Previous Page"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <div className="repo-page-numbers">
                      {paginationDisplay.showLeadingEllipsis && (
                        <span className="repo-page-ellipsis">...</span>
                      )}
                      {paginationDisplay.pages.map((num) => (
                        <button
                          key={num}
                          className={`repo-page-num ${currentPage === num ? "active" : ""}`}
                          onClick={() => setCurrentPage(num)}
                          aria-current={
                            currentPage === num ? "page" : undefined
                          }
                        >
                          {num}
                        </button>
                      ))}
                      {paginationDisplay.showTrailingEllipsis && (
                        <span className="repo-page-ellipsis">...</span>
                      )}
                    </div>
                    <button
                      className="repo-pagination-btn"
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                      title="Next Page"
                    >
                      <ChevronRight size={16} />
                    </button>
                    <button
                      className="repo-pagination-btn"
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage(totalPages)}
                      title="Last Page"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        </div>

        {activeSection === "repository" && selectionRect && (
          <div
            className="repo-selection-box"
            style={{
              left: `${selectionRect.left}px`,
              top: `${selectionRect.top}px`,
              width: `${selectionRect.width}px`,
              height: `${selectionRect.height}px`,
            }}
          />
        )}
      </div>

      <RepoModals
        deleteModal={deleteModal}
        onCloseDeleteModal={() => setDeleteModal({ show: false, items: [] })}
        onDelete={actions.handleDelete}
        moveModal={moveModal}
        onCloseMoveModal={() =>
          setMoveModal({ show: false, items: [], targetFolderId: null })
        }
        onSelectMoveFolder={(folderId) =>
          setMoveModal((prev) => ({ ...prev, targetFolderId: folderId }))
        }
        onConfirmMove={() => {
          actions.handleMove(moveModal.items, moveModal.targetFolderId);
          setMoveModal({ show: false, items: [], targetFolderId: null });
          setSelectedItems(new Set());
        }}
        items={items}
        repoView={repoView}
        createFolderModal={createFolderModal}
        onCloseCreateFolderModal={() =>
          setCreateFolderModal({ show: false, name: "" })
        }
        onCreateFolderNameChange={(name) =>
          setCreateFolderModal((prev) => ({ ...prev, name }))
        }
        onCreateFolder={actions.handleCreateFolder}
        showFirstModal={actions.showFirstModal}
        onCloseFirstModal={() => actions.setShowFirstModal(false)}
        uploadMode={actions.uploadMode}
        onUploadModeSelect={actions.setUploadMode}
        onContinueToTraining={() => {
          actions.setShowFirstModal(false);
          actions.setShowTrainingModal(true);
        }}
        showTrainingModal={actions.showTrainingModal}
        onCloseTrainingModal={() =>
          !actions.isTraining && actions.setShowTrainingModal(false)
        }
        isTraining={actions.isTraining}
        onSendForTraining={() =>
          actions.handleSendForTraining(
            actions.selectedFileToTrain,
            actions.uploadMode,
          )
        }
        showSummaryModal={actions.showSummaryModal}
        onCloseSummaryModal={() => actions.setShowSummaryModal(false)}
        showRenameModal={actions.showRenameModal}
        renameTarget={actions.renameTarget}
        renameText={actions.renameText}
        onRenameTextChange={actions.setRenameText}
        onCloseRenameModal={actions.closeRenameModal}
        onRename={actions.performRename}
        isRenaming={actions.isRenaming}
        dataName={actions.dataName}
        onDataNameChange={actions.setDataName}
        showUploadModeDropdown={actions.showUploadModeDropdown}
        onToggleUploadModeDropdown={() =>
          actions.setShowUploadModeDropdown(!actions.showUploadModeDropdown)
        }
        summaryDataList={actions.summaryDataList}
        onUploadToBackend={actions.uploadToBackend}
        isSubmitting={actions.isSubmitting}
      />
    </DndProvider>
  );
};

export default RepoOrganizer;
